import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Elements,
    useStripe,
    useElements,
    ExpressCheckoutElement,
    type ElementsOptions
} from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import ConditionOverlay from "./ConditionOverlay";
//import { useLocalization } from "@providers/LocalizationProvider";
import { useConsent } from "../../../helpers/useConsent";

/* -------------------- loader con cache stile getStripePromise -------------------- */

const stripePromiseCache = new Map<string, Promise<Stripe | null>>();

function getStripePromise(stripeApikey: string) {
    if (!stripePromiseCache.has(stripeApikey)) {
        stripePromiseCache.set(stripeApikey, loadStripe(stripeApikey));
    }
    return stripePromiseCache.get(stripeApikey)!;
}

/* -------------------- Props -------------------- */

type StripeCheckoutOuterProps = {
    /** La tua publishable key di Stripe */
    stripeApikey: string;
    /** Opzioni Elements (le stesse che passavi prima) */
    elementsOptions: ElementsOptions;
    /** Dati che usi nel create PI, come facevi prima */
    stripeOptions: Record<string, any>;
    /** URL per creare il Payment Intent */
    stripePaymentIntentUrl?: string;
};

/** Wrapper: carica Stripe e fornisce <Elements> */
const StripeCheckout: React.FC<StripeCheckoutOuterProps> = ({
    stripeApikey,
    elementsOptions,
    stripeOptions,
    stripePaymentIntentUrl
}) => {
    // memo per non ricreare la Promise ad ogni render
    const stripePromise = useMemo(
        () => (stripeApikey ? getStripePromise(stripeApikey) : null),
        [stripeApikey]
    );

    if (!stripePromise) return null;

    return (
        <Elements stripe={stripePromise} options={elementsOptions}>
            <StripeCheckoutInner stripeOptions={stripeOptions} stripePaymentIntentUrl={stripePaymentIntentUrl} />
        </Elements>
    );
};

export default StripeCheckout;

/* -------------------- Inner: usa useStripe/useElements -------------------- */

type StripeCheckoutInnerProps = {
    stripeOptions: Record<string, any>;
    stripePaymentIntentUrl?: string;
};
const StripeCheckoutInner: React.FC<StripeCheckoutInnerProps> = ({ stripeOptions, stripePaymentIntentUrl }) => {

    const stripe = useStripe();
    const elements = useElements();

    const [visibility, setVisibility] = useState<"hidden" | "initial">("hidden");
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [hasCalledOnReady, setHasCalledOnReady] = useState(false);

    // Terms & Conditions
    

    const {isAccepted, showError, setShowError, handleCheckboxChange} = useConsent("stripe");

    const [overlayVisible, setOverlayVisible] = useState(false);
    
    const [showRedirectLoading, setShowRedirectLoading] = useState(false);
    const [firstClick, setFirstClick] = useState(false);

    //const { getText } = useLocalization();
    const stripeElementContainerRef = useRef<HTMLDivElement | null>(null);
 
     useEffect(() => {
        if (!isAccepted) return;
        if (!firstClick) setFirstClick(true);
        stripeElementContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, [isAccepted, firstClick]);

      // Nascondi il disclaimer dopo accettazione (opzionale)
 useEffect(() => {
  if (isAccepted && overlayVisible) setOverlayVisible(false);
 }, [isAccepted, overlayVisible]);


    // Timeout di sicurezza per lo spinner
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!hasCalledOnReady) {
                setIsLoading(false);
            }
        }, 8000);
        return () => clearTimeout(timeout);
    }, [hasCalledOnReady]);


    const handleContinue = () => {
        if (!overlayVisible) setOverlayVisible(true);
        if (!firstClick) setFirstClick(true);      // << segna il primo click
        if (!isAccepted) setShowError(true);
    };

 
    const expressCheckoutOptions = {
        emailRequired: true,
        buttonType: {
            googlePay: "checkout",
            applePay: "check-out",
            paypal: "checkout"
        },
        layout: {
            maxColumns: 1,
            overflow: "never" as const
        }
        // shippingRates: [...]
    };

    const onReady = ({ availablePaymentMethods }: any) => {
        if (availablePaymentMethods) {
            setHasCalledOnReady(true);
            setIsLoading(false);
            setVisibility("initial");
        }
    };
 

   

    function getRestNonce(): string | undefined {
        if (typeof window === "undefined") return undefined;
        const w = window as any;

        const cfg = w.__GARFY_CHECKOUT_EXPRESS__;
        const nonce =
            cfg?.nonce ||
            w?.wpApiSettings?.nonce || // classico di WP
            w?.wprest_nonce;           // tuo fallback storico

        if (!nonce) {
            console.warn(
                "[CheckoutExpress] Nessun nonce REST trovato (GARFY/ wpApiSettings / wprest_nonce). La REST sarà anonima."
            );
        }

        return nonce;
    }

    const onConfirm = async () => {
        if (!stripe || !elements) return;

        //  Validazione elementi Stripe
        const { error: submitError } = await elements.submit();
        if (submitError) {
            setErrorMessage(submitError.message);
            return;
        }

        // Payload per il backend (come prima)
        const paymentIntentPayload = {
            amount: stripeOptions.amount,
            currency: stripeOptions.currency,
            metadata: stripeOptions.metadata || {} // es. { context: "checkout" }
        };

        // URL backend: passato dal parent o fallback
        const url =
            stripePaymentIntentUrl ||
            `${import.meta.env.VITE_BACKEND_URL}/wp-json/garfy/v1/stripe/payment-intent`;
 
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };

        const wpNonce = getRestNonce();
        if (wpNonce) {
            headers["X-WP-Nonce"] = wpNonce;
            console.log("[CheckoutExpress] wpNonce usato in createOrder:", wpNonce);
        } else {
            console.warn(
                "[CheckoutExpress] Nessun nonce trovato in __GARFY_CHECKOUT_EXPRESS__, la REST sarà anonima"
            );
        }

        
        // fetch CON credentials: "include" per portare cookie + sessione Woo
        const res = await fetch(url, {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify(paymentIntentPayload),
        });

        // Leggo una sola volta la risposta grezza
        const txt = await res.text();

        let payload: any = null;
        try {
            payload = JSON.parse(txt);
        } catch {
            // lascio payload = null, gestito sotto
        }

        if (!res.ok) {
            if (payload?.redirect) {
                window.location.href = payload.redirect;
                return;
            }
            const msg = payload?.error || `createPaymentIntent ${res.status}: ${txt}`;
            throw new Error(msg);
        }

        // Caso OK: mi aspetto il client_secret nel payload JSON
        const clientSecret = payload?.client_secret;
        if (!clientSecret) {
            throw new Error("Client secret is missing in the response");
        }

        setShowRedirectLoading(true);

        // 9) Conferma PaymentIntent lato Stripe
        const { error } = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
                return_url: `${window.location.origin}/checkout/complete`,
            },
        });

        if (error) {
            console.error("Stripe error:", error);
            setErrorMessage(error.message || "Errore durante il pagamento Stripe.");
            setShowRedirectLoading(false);
        }
    };

     

    return (
        <>
            <div className="mb-3" id="express-checkout-element" style={{ visibility }}>
                <ConditionOverlay
                    visible={overlayVisible}
                    showError={showError}
                    checked={isAccepted}
                    onCheckboxChange={handleCheckboxChange}
                    idPrefix="stripe"
                />
                <div ref={stripeElementContainerRef} style={{ position: "relative" }}>
                    <ExpressCheckoutElement options={expressCheckoutOptions} onConfirm={onConfirm} onReady={onReady} />

                    {!isAccepted && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 9999,
                                backgroundColor: firstClick ? "rgba(252, 252, 252, 0.5)" : "transparent",
                                display: "block",
                                cursor: "pointer"
                            }}
                            onClick={handleContinue}
                            className="acceptance-overlay"
                        />
                    )}
                </div>
            </div>

            {/* {isLoading && (
                <div className="d-flex justify-content-center mb-3">
                    <div className="spinner-grow text-secondary" role="status" />
                </div>
            )} */}

            {showRedirectLoading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                        zIndex: 99999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
              {/*       <div className="spinner-border text-light" role="status" /> */}
                </div>
            )}
        </>
    );
};
